import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchRemoteConfig,
  getRemoteConfigValues,
  REMOTE_CONFIG_KEYS,
  type RemoteConfigValues,
} from '@shared/services/remoteConfigService';
import { auth, db } from '@shared/services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { logError, logWarn } from '@/lib/log';
import { recordRcExposure, type RcFlagMap } from '@/experiments/recordRcExposure';

interface RemoteConfigContextValue extends RemoteConfigValues {
  isLoading: boolean;
  /** Per-user experiment-arm forcing from `featureOverrides/{uid}.experiments`
   *  ({slotKey: variantKey}). Makes featureOverrides the universal "show this
   *  user the feature" lever: useExperiment treats a forced arm exactly like
   *  the localStorage QA override — descriptor and bucketing are bypassed and
   *  the session records nothing (no assignment, no exposure, no render
   *  marker), so flagged internal users can never pollute experiment data. */
  experimentOverrides: Record<string, string>;
  /** True once the featureOverrides fetch for the current auth state has
   *  settled (found, absent, signed out, or failed). Consumers that must not
   *  flash the un-overridden experience (useExperiment) wait on this. */
  overridesSettled: boolean;
}

/** @internal Exported for demo mode — do not use directly in app components */
export const RemoteConfigContext = createContext<RemoteConfigContextValue | null>(null);

/** Map from Firestore field names (snake_case) to RemoteConfigValues keys (camelCase) */
const OVERRIDE_FIELD_MAP: Record<string, keyof RemoteConfigValues> = {
  [REMOTE_CONFIG_KEYS.GOALS_ENABLED]: 'goalsEnabled',
  [REMOTE_CONFIG_KEYS.SUBSCRIPTIONS_ENABLED]: 'subscriptionsEnabled',
  [REMOTE_CONFIG_KEYS.PAYMENTS_ENABLED]: 'paymentsEnabled',
  [REMOTE_CONFIG_KEYS.CART_COMPANION_ENABLED]: 'cartCompanionEnabled',
  [REMOTE_CONFIG_KEYS.CART_COMPANION_SECOND_LOOK_ENABLED]: 'cartCompanionSecondLookEnabled',
  [REMOTE_CONFIG_KEYS.PUSH_SOFT_ASK_ENABLED]: 'pushSoftAskEnabled',
  [REMOTE_CONFIG_KEYS.PUSH_SOFT_ASK_CONFIG]: 'pushSoftAskConfig',
  [REMOTE_CONFIG_KEYS.MIN_IOS_VERSION]: 'minIosVersion',
  [REMOTE_CONFIG_KEYS.MIN_ANDROID_VERSION]: 'minAndroidVersion',
  [REMOTE_CONFIG_KEYS.FORCE_UPDATE_BELOW_IOS]: 'forceUpdateBelowIos',
  [REMOTE_CONFIG_KEYS.FORCE_UPDATE_BELOW_ANDROID]: 'forceUpdateBelowAndroid',
  [REMOTE_CONFIG_KEYS.INSIGHTS_FEED_ENABLED]: 'insightsFeedEnabled',
  [REMOTE_CONFIG_KEYS.DEBT_MANAGER_ENABLED]: 'debtManagerEnabled',
  [REMOTE_CONFIG_KEYS.QUIET_DAYS_ENABLED]: 'quietDaysEnabled',
  [REMOTE_CONFIG_KEYS.NO_SPEND_WEEKEND_ENABLED]: 'noSpendWeekendEnabled',
  [REMOTE_CONFIG_KEYS.NO_SPEND_LIVE_ACTIVITY_ENABLED]: 'noSpendLiveActivityEnabled',
  [REMOTE_CONFIG_KEYS.NO_SPEND_ANDROID_REMINDER_ENABLED]: 'noSpendAndroidReminderEnabled',
  [REMOTE_CONFIG_KEYS.MFA_ENABLED]: 'mfaEnabled',
  [REMOTE_CONFIG_KEYS.SUBSCRIPTION_ANOMALIES_ENABLED]: 'subscriptionAnomaliesEnabled',
  [REMOTE_CONFIG_KEYS.APP_REVIEW_PROMPT_ENABLED]: 'appReviewPromptEnabled',
  [REMOTE_CONFIG_KEYS.APP_REVIEW_MIN_ACTIVITY_DAYS]: 'appReviewMinActivityDays',
  [REMOTE_CONFIG_KEYS.SHARE_CARDS_ENABLED]: 'shareCardsEnabled',
  [REMOTE_CONFIG_KEYS.SINGLE_ACCOUNT_CUTOFF]: 'singleAccountCutoff',
  [REMOTE_CONFIG_KEYS.WHERE_IT_GOES_ENABLED]: 'whereItGoesEnabled',
  [REMOTE_CONFIG_KEYS.FOUND_MONEY_ENABLED]: 'foundMoneyEnabled',
  [REMOTE_CONFIG_KEYS.UNDERSTANDING_MOMENT_ENABLED]: 'understandingMomentEnabled',
  [REMOTE_CONFIG_KEYS.MEMBERSHIP_ROW_ENABLED]: 'membershipRowEnabled',
};

export const RemoteConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [values, setValues] = useState<RemoteConfigValues | null>(null);
  const [overrides, setOverrides] = useState<Partial<RemoteConfigValues>>({});
  const [experimentOverrides, setExperimentOverrides] = useState<Record<string, string>>({});
  const [overridesSettled, setOverridesSettled] = useState(false);
  // Set only once the overrides fetch for this uid has SETTLED (found, absent,
  // or failed) — gates exposure logging so a transient defaults-only snapshot
  // never writes a churn row before overrides arrive.
  const [exposureUid, setExposureUid] = useState<string | null>(null);
  const prevUidRef = useRef<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      await fetchRemoteConfig();
      setValues(getRemoteConfigValues());
    } catch (error) {
      logError(error, { context: 'load_remote_config' });
      setValues({
        goalsEnabled: true,
        subscriptionsEnabled: false,
        paymentsEnabled: false,
        cartCompanionEnabled: false,
        cartCompanionSecondLookEnabled: false,
        pushSoftAskEnabled: false,
        pushSoftAskConfig: '',
        minIosVersion: '',
        minAndroidVersion: '',
        forceUpdateBelowIos: '',
        forceUpdateBelowAndroid: '',
        insightsFeedEnabled: false,
        debtManagerEnabled: false,
        quietDaysEnabled: false,
        noSpendWeekendEnabled: false,
        noSpendLiveActivityEnabled: false,
        noSpendAndroidReminderEnabled: false,
        mfaEnabled: false,
        subscriptionAnomaliesEnabled: false,
        appReviewPromptEnabled: false,
        appReviewMinActivityDays: 10,
        experimentsKillSwitch: false,
        shareCardsEnabled: false,
        singleAccountCutoff: '',
        whereItGoesEnabled: false,
        foundMoneyEnabled: false,
        understandingMomentEnabled: false,
        membershipRowEnabled: false,
      });
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Re-fetch when iOS app returns to foreground (closing/reopening resumes WebView without remounting)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        loadConfig();
      }
    });
    return () => { listener.then(h => h.remove()); };
  }, [loadConfig]);

  // Fetch per-user feature overrides when authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        prevUidRef.current = null;
        setOverrides({});
        setExperimentOverrides({});
        setExposureUid(null);
        setOverridesSettled(true); // nothing to fetch — signed-out is settled
        return;
      }
      // Demo uses mock AuthContext; real Firebase user may still be signed in.
      // Never read featureOverrides from Firestore on demo routes (rules + noise).
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/demo')) {
        prevUidRef.current = null;
        setOverrides({});
        setExperimentOverrides({});
        setExposureUid(null);
        setOverridesSettled(true);
        return;
      }
      // Skip if we already fetched for this user
      if (user.uid === prevUidRef.current) return;
      prevUidRef.current = user.uid;

      // Reset before fetching so a uid switch never leaks the previous
      // account's overrides, and mark unsettled while the read is in flight.
      setOverrides({});
      setExperimentOverrides({});
      setOverridesSettled(false);
      try {
        const snap = await getDoc(doc(db, 'featureOverrides', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          const parsed: Partial<RemoteConfigValues> = {};
          for (const [firestoreKey, configKey] of Object.entries(OVERRIDE_FIELD_MAP)) {
            if (firestoreKey in data) {
              (parsed as Record<string, unknown>)[configKey] = data[firestoreKey];
            }
          }
          setOverrides(parsed);
          // Experiment-arm forcing: `experiments` map of {slotKey: variantKey}.
          // Applied verbatim (a slot the app doesn't know is simply never
          // consulted); non-string values are ignored as malformed.
          const exp = data.experiments;
          if (exp && typeof exp === 'object') {
            const parsedExp: Record<string, string> = {};
            for (const [slot, arm] of Object.entries(exp as Record<string, unknown>)) {
              if (typeof arm === 'string' && arm) {
                parsedExp[slot] = arm;
              } else {
                logWarn(`featureOverrides.experiments.${slot} ignored — variant must be a non-empty string`);
              }
            }
            setExperimentOverrides(parsedExp);
          }
        }
      } catch (error) {
        logWarn('Failed to fetch feature overrides:', error);
      }
      setOverridesSettled(true);
      setExposureUid(user.uid);
    });
    return unsubscribe;
  }, []);

  // Stamp the resolved flag snapshot for internal analytics (change-log at
  // userContent/{uid}/rcExposures — see recordRcExposure for write economy).
  // Runs only once RC values are loaded AND the overrides fetch has settled;
  // hash dedupe inside recordRcExposure makes re-renders/re-fetches free.
  useEffect(() => {
    if (!values || !exposureUid) return;
    const resolved: RcFlagMap = {};
    for (const [key, value] of Object.entries(values)) {
      resolved[key] =
        (overrides as Record<string, boolean | number | string | undefined>)[key] ?? value;
    }
    recordRcExposure(exposureUid, resolved);
  }, [values, overrides, exposureUid]);

  const contextValue: RemoteConfigContextValue = {
    goalsEnabled: overrides.goalsEnabled ?? values?.goalsEnabled ?? true,
    subscriptionsEnabled: overrides.subscriptionsEnabled ?? values?.subscriptionsEnabled ?? false,
    paymentsEnabled: overrides.paymentsEnabled ?? values?.paymentsEnabled ?? false,
    cartCompanionEnabled: overrides.cartCompanionEnabled ?? values?.cartCompanionEnabled ?? false,
    cartCompanionSecondLookEnabled: overrides.cartCompanionSecondLookEnabled ?? values?.cartCompanionSecondLookEnabled ?? false,
    pushSoftAskEnabled: overrides.pushSoftAskEnabled ?? values?.pushSoftAskEnabled ?? false,
    pushSoftAskConfig: overrides.pushSoftAskConfig ?? values?.pushSoftAskConfig ?? '',
    minIosVersion: overrides.minIosVersion ?? values?.minIosVersion ?? '',
    minAndroidVersion: overrides.minAndroidVersion ?? values?.minAndroidVersion ?? '',
    forceUpdateBelowIos: overrides.forceUpdateBelowIos ?? values?.forceUpdateBelowIos ?? '',
    forceUpdateBelowAndroid: overrides.forceUpdateBelowAndroid ?? values?.forceUpdateBelowAndroid ?? '',
    insightsFeedEnabled: overrides.insightsFeedEnabled ?? values?.insightsFeedEnabled ?? false,
    debtManagerEnabled: overrides.debtManagerEnabled ?? values?.debtManagerEnabled ?? false,
    quietDaysEnabled: overrides.quietDaysEnabled ?? values?.quietDaysEnabled ?? false,
    noSpendWeekendEnabled: overrides.noSpendWeekendEnabled ?? values?.noSpendWeekendEnabled ?? false,
    noSpendLiveActivityEnabled: overrides.noSpendLiveActivityEnabled ?? values?.noSpendLiveActivityEnabled ?? false,
    noSpendAndroidReminderEnabled: overrides.noSpendAndroidReminderEnabled ?? values?.noSpendAndroidReminderEnabled ?? false,
    mfaEnabled: overrides.mfaEnabled ?? values?.mfaEnabled ?? false,
    subscriptionAnomaliesEnabled: overrides.subscriptionAnomaliesEnabled ?? values?.subscriptionAnomaliesEnabled ?? false,
    appReviewPromptEnabled: overrides.appReviewPromptEnabled ?? values?.appReviewPromptEnabled ?? false,
    appReviewMinActivityDays: overrides.appReviewMinActivityDays ?? values?.appReviewMinActivityDays ?? 10,
    experimentsKillSwitch: overrides.experimentsKillSwitch ?? values?.experimentsKillSwitch ?? false,
    shareCardsEnabled: overrides.shareCardsEnabled ?? values?.shareCardsEnabled ?? false,
    singleAccountCutoff: overrides.singleAccountCutoff ?? values?.singleAccountCutoff ?? '',
    whereItGoesEnabled: overrides.whereItGoesEnabled ?? values?.whereItGoesEnabled ?? false,
    foundMoneyEnabled: overrides.foundMoneyEnabled ?? values?.foundMoneyEnabled ?? false,
    understandingMomentEnabled: overrides.understandingMomentEnabled ?? values?.understandingMomentEnabled ?? false,
    membershipRowEnabled: overrides.membershipRowEnabled ?? values?.membershipRowEnabled ?? false,
    isLoading: values === null,
    experimentOverrides,
    overridesSettled,
  };

  return (
    <RemoteConfigContext.Provider value={contextValue}>
      {children}
    </RemoteConfigContext.Provider>
  );
};

export const useRemoteConfig = (): RemoteConfigContextValue => {
  const ctx = useContext(RemoteConfigContext);
  if (!ctx) {
    throw new Error('useRemoteConfig must be used within RemoteConfigProvider');
  }
  return ctx;
};

/** Feature flag keys (from Remote Config) */
export type FeatureFlagKey =
  | 'goals'
  | 'subscriptions'
  | 'payments'
  | 'cartCompanion'
  | 'cartCompanionSecondLook'
  | 'insightsFeed'
  | 'debtManager'
  | 'quietDays'
  | 'noSpendWeekend'
  | 'noSpendLiveActivity'
  | 'noSpendAndroidReminder'
  | 'mfa'
  | 'subscriptionAnomalies'
  | 'shareCards'
  | 'whereItGoes';

/** Hook for feature flags - replaces isFeatureEnabled from featureFlags.ts */
export const useFeatureFlags = () => {
  const {
    goalsEnabled,
    subscriptionsEnabled,
    paymentsEnabled,
    cartCompanionEnabled,
    cartCompanionSecondLookEnabled,
    insightsFeedEnabled,
    debtManagerEnabled,
    quietDaysEnabled,
    noSpendWeekendEnabled,
    noSpendLiveActivityEnabled,
    noSpendAndroidReminderEnabled,
    mfaEnabled,
    subscriptionAnomaliesEnabled,
    shareCardsEnabled,
    whereItGoesEnabled,
    understandingMomentEnabled,
  } = useRemoteConfig();

  const isFeatureEnabled = useCallback((key: string): boolean => {
    if (key === 'goals') return goalsEnabled;
    if (key === 'subscriptions') return subscriptionsEnabled;
    if (key === 'payments') return paymentsEnabled;
    if (key === 'cartCompanion') return cartCompanionEnabled;
    if (key === 'cartCompanionSecondLook') return cartCompanionEnabled && cartCompanionSecondLookEnabled;
    if (key === 'insightsFeed') return insightsFeedEnabled;
    if (key === 'debtManager') return debtManagerEnabled;
    if (key === 'quietDays') return quietDaysEnabled;
    // Event-layer flag: No-Spend Weekend banners + free-user enablement.
    if (key === 'noSpendWeekend') return noSpendWeekendEnabled;
    if (key === 'noSpendLiveActivity') return noSpendLiveActivityEnabled;
    if (key === 'noSpendAndroidReminder') return noSpendAndroidReminderEnabled;
    if (key === 'mfa') return mfaEnabled;
    if (key === 'subscriptionAnomalies') return subscriptionAnomaliesEnabled;
    if (key === 'shareCards') return shareCardsEnabled;
    if (key === 'whereItGoes') return whereItGoesEnabled;
    return false;
  }, [
    goalsEnabled,
    subscriptionsEnabled,
    paymentsEnabled,
    cartCompanionEnabled,
    cartCompanionSecondLookEnabled,
    insightsFeedEnabled,
    debtManagerEnabled,
    quietDaysEnabled,
    noSpendWeekendEnabled,
    noSpendLiveActivityEnabled,
    noSpendAndroidReminderEnabled,
    mfaEnabled,
    subscriptionAnomaliesEnabled,
    shareCardsEnabled,
    whereItGoesEnabled,
  ]);

  const cartCompanionSecondLookActive = cartCompanionEnabled && cartCompanionSecondLookEnabled;

  return {
    isFeatureEnabled,
    goalsEnabled,
    subscriptionsEnabled,
    paymentsEnabled,
    cartCompanionEnabled,
    cartCompanionSecondLookEnabled,
    cartCompanionSecondLookActive,
    insightsFeedEnabled,
    debtManagerEnabled,
    quietDaysEnabled,
    noSpendWeekendEnabled,
    noSpendLiveActivityEnabled,
    noSpendAndroidReminderEnabled,
    mfaEnabled,
    subscriptionAnomaliesEnabled,
    shareCardsEnabled,
    whereItGoesEnabled,
    understandingMomentEnabled,
  };
};
